import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export interface UiPreferences {
  shippingGuideBannerDismissed: boolean;
  hasSeenTradeConfirmModal: boolean;
  hasSeenFirstTradeCelebration: boolean;
  dismissedBonusTaskKeys?: string[];
  /** Homepage post-50 want-list banner dismissed (ISO timestamp); null clears it. Read back via the checklist. */
  homeWantBannerDismissedAt?: string | null;
  /**
   * Suppresses the Complete Trade confirmation modal (Asana 1219704178739168).
   * Absent or false shows the modal; true skips it. Server-side so it follows the
   * user across browsers; never mirrored to localStorage.
   */
  hideCompleteTradeConfirmation?: boolean;
}

export const uiPreferencesApi = createApi({
  reducerPath: 'uiPreferencesApi',
  baseQuery: baseQuery,
  tagTypes: ['UiPreferences'],
  endpoints: (builder) => ({
    getUiPreferences: builder.query<UiPreferences, void>({
      query: () => ({
        url: '/settings/ui-preferences',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['UiPreferences'],
    }),

    updateUiPreferences: builder.mutation<
      { success: boolean; settings: UiPreferences },
      Partial<UiPreferences>
    >({
      query: (body) => ({
        url: '/settings/ui-preferences',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      // Patch the cache at once and undo on failure, the settingsApi pattern, so
      // a Settings toggle flips immediately and reverts if the PUT is rejected.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          uiPreferencesApi.util.updateQueryData('getUiPreferences', undefined, (draft) => {
            Object.assign(draft, arg);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['UiPreferences'],
    }),
  }),
});

export const {
  useGetUiPreferencesQuery,
  useUpdateUiPreferencesMutation,
} = uiPreferencesApi;
